import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollViewInstance,
} from "react-native";

/** How close to the bottom still counts as "at the bottom", in px. */
const PIN_THRESHOLD = 60;

/**
 * Keeps the view pinned to the newest message — but only when the user is already
 * there.
 *
 * The streaming bubble grows continuously, and scrolling to the bottom on every
 * flush would yank the viewport out from under someone who has scrolled up to
 * re-read something earlier in the conversation.
 */
const useAgentScroll = () => {
  const ref = useRef<ScrollViewInstance>(null);
  const pinned = useRef(true);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - (contentOffset.y + layoutMeasurement.height);
      pinned.current = distanceFromBottom <= PIN_THRESHOLD;
    },
    [],
  );

  const scrollToBottom = useCallback((force = false) => {
    if (!force && !pinned.current) return;
    ref.current?.scrollToEnd({ animated: true });
  }, []);

  // `ref`/`onScroll`/`scrollToBottom`/`pinned` are all stable across renders, but
  // a fresh object literal here is not — and callers that depend on the whole
  // returned object (the attention-scroll effect in AgentChat) would re-arm their
  // rAF/timeout on every unrelated re-render without this.
  return useMemo(
    () => ({ ref, onScroll, scrollToBottom, isPinned: pinned }),
    [onScroll, scrollToBottom],
  );
};

/**
 * Force the view to the bottom whenever `value` becomes truthy or changes.
 *
 * For the moments the chat has just changed in a way the person needs to see — a turn
 * failed, a card appeared — where scrolling only when already pinned would leave it
 * below the fold, indistinguishable from a turn that is still thinking.
 *
 * Two frames, then a timeout: the real height is not known until markdown has laid
 * out, and scrolling to an end that has not been measured yet goes nowhere.
 */
export const useForceScrollOn = (
  scroll: ReturnType<typeof useAgentScroll>,
  value: unknown,
) => {
  useEffect(() => {
    if (!value) return;
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => scroll.scrollToBottom(true)),
    );
    const settled = setTimeout(() => scroll.scrollToBottom(true), 150);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settled);
    };
  }, [value, scroll]);
};

export default useAgentScroll;
